import { Button, Dropdown, MenuProps, Tooltip } from 'antd';
import clsx from 'clsx';
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import File from '../../interfaces/File';
import { filteredFileActions } from '../../lib/fileActions';
import { Icon } from '../Core/Icon';
import { useTags } from '../../hooks/useTags';
import { useRightPanel } from '../../hooks/useRightPanel';
import FileAction from '../../interfaces/FileAction';

interface Props {
  handleFileMenuAction: (ids: number[], action: FileMenuActionEnum) => void;
  files: File[];
  className?: string;
  isTopbar?: boolean;
  isUploadDisabledInCurrentFolder: boolean;
  filesActions: FileAction[];
}

const FilesActions: React.FC<Props> = ({
  handleFileMenuAction,
  files,
  className,
  isTopbar = false,
  isUploadDisabledInCurrentFolder = false,
  filesActions,
}) => {
  const { t } = useTranslation();

  // Refs for the container and the overflow button
  const containerRef = useRef<HTMLDivElement>(null);
  const overflowButtonRef = useRef<HTMLButtonElement>(null);
  const actionButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // This state variable holds the index of the first visible action.
  // It starts at 0 and is recalculated in useLayoutEffect.
  const [overflowStart, setOverflowStart] = useState(0);

  const fileIds = useMemo(() => files.map((f) => f.uid), [files]);

  const setActionButtonRef = useCallback(
    (key: string) => (element: HTMLButtonElement | null) => {
      if (element) {
        actionButtonRefs.current.set(key, element);
      }
    },
    []
  );

  const overflowActions = useMemo(
    () => filesActions.slice(overflowStart),
    [filesActions, overflowStart]
  );

  // Ricalcola l’indice di overflow usando ResizeObserver
  const measureItems = useCallback(() => {
    const container = containerRef.current;
    const overflowBtn = overflowButtonRef.current;
    const refsCount = actionButtonRefs.current.size;

    if (
      !container ||
      filesActions.length === 0 ||
      refsCount < filesActions.length
    ) {
      setOverflowStart(filesActions.length);
      return;
    }

    const containerWidth = container.clientWidth;

    let overflowBtnWidth = 0;
    if (overflowBtn) {
      const styleO = window.getComputedStyle(overflowBtn);
      overflowBtnWidth =
        overflowBtn.offsetWidth + parseFloat(styleO.marginRight || '0');
    }

    let usedWidth = 0;
    let cutIndex = filesActions.length;

    for (let i = 0; i < filesActions.length; i++) {
      const action = filesActions[i];
      const btn = actionButtonRefs.current.get(action.key);

      if (!btn) {
        cutIndex = i;
        break;
      }

      let btnWidth = 0;
      if (btn.dataset.calculatedWidth) {
        btnWidth = parseFloat(btn.dataset.calculatedWidth);
      } else {
        if (btn.classList.contains('hidden')) {
          btn.style.display = 'flex';
        }
        const styleB = window.getComputedStyle(btn);
        btnWidth =
          btn.clientWidth +
          parseFloat(styleB.marginRight || '0') +
          parseFloat(styleB.borderLeftWidth || '0') +
          parseFloat(styleB.borderRightWidth || '0');
        btn.dataset.calculatedWidth = btnWidth.toString();
        btn.style.display = '';
      }

      // Aggiungo lo spazio per il dropdown solo se dopo questo elemento
      // ci sarà effettivamente overflow
      const needsOverflowBtn = i < filesActions.length - 1;
      const nextTotal = Math.round(
        usedWidth + btnWidth + (needsOverflowBtn ? overflowBtnWidth : 0)
      );

      // Se supero, tutto da qui in poi va in overflow
      if (nextTotal >= containerWidth) {
        cutIndex = i;
        break;
      }

      usedWidth += btnWidth;
    }

    setOverflowStart(cutIndex);
  }, [filesActions]);

  // Configura ResizeObserver e esegui la misurazione iniziale
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Configura l'observer per misurare quando il container cambia dimensione
    const observer = new ResizeObserver(() => {
      measureItems(); // Ricalcola l'overflow
    });

    // Osserva il container principale
    observer.observe(container);

    // Esegui una misurazione iniziale dopo che il DOM è stato aggiornato e gli elementi sono renderizzati
    // Per garantire che i riferimenti ai pulsanti siano popolati prima della misurazione
    if (filesActions.length > 0) {
      // Aggiungi un piccolo timeout o controlla che i refs siano pronti se measureItems fallisce all'inizio
      // Per semplicità, affidiamoci a useLayoutEffect che corre dopo il mount/update e ai controlli dentro measureItems.
      measureItems();
    } else {
      setOverflowStart(0); // Nessuna azione, nessun elemento visibile
    }

    return () => {
      observer.disconnect();
    };
  }, [measureItems, filesActions]); // Ri-esegui l'effetto se la funzione measureItems o le azioni cambiano

  /**
   * Memoized function to generate menu items for a given array of actions.
   *
   * This function takes an array of actions and maps them to menu items.
   * Each action is converted to a menu item with the following properties:
   * - `key`: The unique identifier for the action.
   * - `icon`: The icon for the action.
   * - `label`: The label for the action.
   * - `danger`: Whether the action is dangerous.
   * - `disabled`: Whether the action is disabled.
   * - `children`: The child actions for the action.
   * - `onClick`: The function to call when the action is clicked.
   *
   * Dependencies:
   * - `fileIds`: The IDs of the files.
   * - `handleFileMenuAction`: The function to call when the action is clicked.
   * - `t`: The translation function.
   *
   * Returns:
   * - An array of menu items.
   */
  const getItems = useCallback(
    (actions: FileAction[]): MenuProps['items'] =>
      actions.map((a) => {
        const has = a.children?.length;
        return {
          key: a.key,
          icon: a.icon,
          label: a.label,
          danger: a.danger,
          disabled: a.disabled,
          children: has ? getItems(a.children!) : undefined,
          onClick: () => {
            if (!has)
              handleFileMenuAction(fileIds, a.key as FileMenuActionEnum);
          },
        };
      }),
    [fileIds, handleFileMenuAction, t]
  );

  // Se non ci sono azioni disponibili, mostra un messaggio
  if (!filesActions.length) {
    return (
      <span className={clsx('flex-grow px-2', className)}>
        {t('common.noActionsAvailable')}
      </span>
    );
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'flex flex-1 items-center gap-1 overflow-hidden whitespace-nowrap',
        className
      )}
      style={{ minWidth: 0 }}
    >
      {filesActions.map((a, index) => {
        const isVisible = index <= overflowStart - 1;

        if (a.children && a.children.length > 0) {
          return (
            <Tooltip key={a.key} title={t(String(a.label))}>
              <Dropdown
                menu={{ items: getItems(a.children) }}
                trigger={['hover']}
              >
                <Button
                  ref={setActionButtonRef(a.key)}
                  type="text"
                  icon={a.icon}
                  disabled={a.disabled}
                  danger={a.danger}
                  className={clsx({ hidden: !isVisible })}
                >
                  {t(String(a.label))}
                </Button>
              </Dropdown>
            </Tooltip>
          );
        }
        return (
          <Tooltip key={a.key} title={t(String(a.label))}>
            <Button
              ref={setActionButtonRef(a.key)}
              type="text"
              icon={a.icon}
              disabled={a.disabled}
              danger={a.danger}
              onClick={() =>
                handleFileMenuAction(fileIds, a.key as FileMenuActionEnum)
              }
              className={clsx({ hidden: !isVisible })}
            >
              {t(String(a.label))}
            </Button>
          </Tooltip>
        );
      })}

      <div
        ref={overflowButtonRef as any}
        style={{
          visibility:
            filesActions.length > overflowStart ? 'visible' : 'hidden',
        }}
        className="flex items-center"
      >
        <Dropdown
          menu={{ items: getItems(overflowActions) }}
          trigger={['click']}
        >
          <Button type="text">
            <Icon name="EllipsisVerticalIcon" className="w-5" />
          </Button>
        </Dropdown>
      </div>
    </div>
  );
};

export default FilesActions;
